import { useEffect, useMemo, useState } from "react";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Filter, CalendarSearch } from "lucide-react";
import { api } from "@/lib/api";
import {
  adminListSessions,
  fmtDateTime,
  statusBadge,
  typeBadge,
} from "@/lib/schedule";
import { listAdminCourses } from "@/lib/courses";

const STATUSES = ["all", "scheduled", "live", "completed", "cancelled"];

export default function AdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [teacherId, setTeacherId] = useState("all");
  const [courseId, setCourseId] = useState("all");
  const [status, setStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Static lookups
  useEffect(() => {
    Promise.all([
      listAdminCourses(),
      api.get("/admin/users", { params: { role: "teacher" } }),
    ])
      .then(([cs, us]) => {
        setCourses(cs);
        setTeachers(us.data);
      })
      .catch(() => {});
  }, []);

  // Sessions
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (teacherId !== "all") params.teacher_id = teacherId;
    if (courseId !== "all") params.course_id = courseId;
    if (fromDate) params.from = new Date(fromDate).toISOString();
    if (toDate) {
      const t = new Date(toDate);
      t.setHours(23, 59, 59, 999);
      params.to = t.toISOString();
    }
    adminListSessions(params)
      .then((rows) => {
        const filtered = status === "all" ? rows : rows.filter((r) => r.status === status);
        setSessions(filtered);
      })
      .finally(() => setLoading(false));
  }, [teacherId, courseId, fromDate, toDate, status]);

  const courseTitleById = useMemo(() => {
    const map = new Map();
    courses.forEach((c) => map.set(c.id, c.title));
    return map;
  }, [courses]);
  const teacherNameById = useMemo(() => {
    const map = new Map();
    teachers.forEach((t) => map.set(t.id, t.name));
    return map;
  }, [teachers]);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="admin-sessions">
      <DashboardHeader subtitle="Admin · All sessions" />
      <main className="mx-auto max-w-7xl px-6 py-8 space-y-4">
        <BackButton to="/admin" />
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <CalendarSearch className="h-6 w-6 text-amber-600" /> Sessions
              across teachers
            </h1>
            <p className="text-sm text-stone-500">
              Read-only view of every live class scheduled in KKS.
            </p>
          </div>
        </div>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4 text-stone-500" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <div className="space-y-1">
                <Label>Teacher</Label>
                <Select value={teacherId} onValueChange={setTeacherId}>
                  <SelectTrigger data-testid="filter-teacher">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All teachers</SelectItem>
                    {teachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} · {t.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Course</Label>
                <Select value={courseId} onValueChange={setCourseId}>
                  <SelectTrigger data-testid="filter-course">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All courses</SelectItem>
                    {courses.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s === "all" ? "All" : s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>From</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  data-testid="filter-from"
                />
              </div>
              <div className="space-y-1">
                <Label>To</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  data-testid="filter-to"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-base">
              {sessions.length} session{sessions.length === 1 ? "" : "s"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center text-stone-500">
                <Loader2 className="mx-auto h-5 w-5 animate-spin mb-2" /> Loading…
              </div>
            ) : sessions.length === 0 ? (
              <div className="py-12 text-center text-sm text-stone-500" data-testid="no-admin-sessions">
                No sessions match these filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-sessions-table">
                  <thead className="bg-stone-50 text-stone-600 text-xs uppercase">
                    <tr>
                      <th className="text-left py-2 px-3">When</th>
                      <th className="text-left py-2 px-3">Title</th>
                      <th className="text-left py-2 px-3">Course</th>
                      <th className="text-left py-2 px-3">Teacher</th>
                      <th className="text-left py-2 px-3">Type</th>
                      <th className="text-left py-2 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s) => (
                      <tr
                        key={s.id}
                        className="border-t border-stone-100"
                        data-testid={`admin-session-row-${s.id}`}
                      >
                        <td className="py-2 px-3 text-stone-700">
                          {fmtDateTime(s.starts_at)}
                        </td>
                        <td className="py-2 px-3 font-medium text-stone-900">
                          {s.title}
                        </td>
                        <td className="py-2 px-3 text-stone-600">
                          {courseTitleById.get(s.course_id) || s.course_id.slice(0, 8)}
                        </td>
                        <td className="py-2 px-3 text-stone-600">
                          {teacherNameById.get(s.teacher_id) ||
                            s.teacher_id.slice(0, 8)}
                        </td>
                        <td className="py-2 px-3">
                          <Badge
                            variant="outline"
                            className={typeBadge(s.session_type)}
                          >
                            {s.session_type}
                          </Badge>
                        </td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className={statusBadge(s.status)}>
                            {s.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
